import { Link } from "react-router-dom";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { SEO } from "@/components/SEO";

const Section = ({ title, children, id }) => (
  <section id={id} className="mb-12" data-testid={`legal-section-${id}`}>
    <h2 className="font-display text-2xl md:text-[28px] tracking-tight text-ink mb-5">
      {title}
    </h2>
    <div className="space-y-4 text-[15px] text-slate-700 leading-relaxed">
      {children}
    </div>
  </section>
);

export default function PrivacyPage() {
  const lastUpdated = "May 2026";

  return (
    <main className="bg-white text-ink min-h-screen" data-testid="privacy-page">
      <SEO
        title="Privacy Policy — Gradatim"
        description="How Gradatim IT Ventures India Private Limited collects, uses and protects your personal information."
        path="/privacy"
      />
      <Nav />

      <section className="bg-cream border-b border-hairline">
        <div className="max-w-[840px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-14 md:pb-20">
          <div className="overline text-copper mb-4">Legal</div>
          <h1 className="font-display font-medium text-4xl md:text-[52px] tracking-tight leading-[1.05] text-ink">
            Privacy Policy
          </h1>
          <p className="text-[14px] text-slate-500 mt-5">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-[840px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <Section id="intro" title="1. Introduction">
            <p>
              <strong>Gradatim IT Ventures India Private Limited</strong>{" "}
              ("Gradatim", "we", "us", or "our") is committed to protecting your
              privacy. This Privacy Policy explains how we collect, use,
              disclose and safeguard information when you visit{" "}
              <Link to="/" className="text-copper hover:underline">
                www.gradatim.co.in
              </Link>{" "}
              or interact with our services.
            </p>
            <p>
              By using this website, you agree to the collection and use of
              information in accordance with this policy.
            </p>
          </Section>

          <Section id="info-collected" title="2. Information we collect">
            <p>
              We collect information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc list-outside pl-5 space-y-2">
              <li>Fill out the contact / demo request form (name, email, company, message)</li>
              <li>Subscribe to communications</li>
              <li>Engage with us via email at innovate@gradatim.co.in</li>
            </ul>
            <p>
              We also automatically collect certain information through cookies
              and similar technologies, including IP address, browser type,
              device information, pages visited and referring URL — only after
              you grant consent via our cookie banner.
            </p>
          </Section>

          <Section id="how-used" title="3. How we use your information">
            <ul className="list-disc list-outside pl-5 space-y-2">
              <li>To respond to your enquiries and demo requests</li>
              <li>To provide and improve our services and platform</li>
              <li>To send relevant business communications (only with consent)</li>
              <li>To analyse site usage and improve user experience</li>
              <li>To comply with legal obligations</li>
            </ul>
          </Section>

          <Section id="sharing" title="4. Sharing and disclosure">
            <p>
              We do not sell your personal data. We may share information with
              trusted service providers who help us operate our website (for
              example, analytics and email delivery), under appropriate
              confidentiality and security obligations.
            </p>
            <p>
              We may also disclose information when required by law, regulation,
              court order or to protect our rights, property or safety.
            </p>
          </Section>

          <Section id="cookies" title="5. Cookies and tracking">
            <p>
              We use cookies and similar technologies to recognise your browser
              and remember your preferences. You can accept or decline these
              via the cookie banner displayed on your first visit. Declining
              will not load analytics scripts.
            </p>
          </Section>

          <Section id="security" title="6. Data security">
            <p>
              We use reasonable administrative, technical and physical
              safeguards to protect your information. However, no method of
              transmission over the internet is fully secure, and we cannot
              guarantee absolute security.
            </p>
          </Section>

          <Section id="retention" title="7. Data retention">
            <p>
              We retain personal data only as long as necessary for the
              purposes set out in this policy, or as required by applicable law.
            </p>
          </Section>

          <Section id="rights" title="8. Your rights">
            <p>
              Depending on your jurisdiction (including under India's DPDP Act
              and the EU GDPR where applicable), you may have the right to:
            </p>
            <ul className="list-disc list-outside pl-5 space-y-2">
              <li>Access, correct or delete your personal information</li>
              <li>Object to or restrict processing</li>
              <li>Withdraw consent at any time</li>
              <li>Lodge a complaint with the relevant supervisory authority</li>
            </ul>
            <p>
              To exercise these rights, email us at{" "}
              <a href="mailto:innovate@gradatim.co.in" className="text-copper hover:underline">
                innovate@gradatim.co.in
              </a>
              .
            </p>
          </Section>

          <Section id="third-party" title="9. Third-party links">
            <p>
              Our site may contain links to third-party websites. We are not
              responsible for their privacy practices and encourage you to
              review their policies.
            </p>
          </Section>

          <Section id="changes" title="10. Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time. Any changes
              will be reflected on this page along with an updated "Last
              updated" date.
            </p>
          </Section>

          <Section id="contact" title="11. Contact us">
            <p>
              <strong>Gradatim IT Ventures India Private Limited</strong>
              <br />
              India
              <br />
              Email:{" "}
              <a href="mailto:innovate@gradatim.co.in" className="text-copper hover:underline">
                innovate@gradatim.co.in
              </a>
            </p>
            <p>
              This document is governed by the laws of India and disputes
              arising hereunder are subject to the exclusive jurisdiction of
              courts in India.
            </p>
          </Section>
        </div>
      </section>

      <Footer />
    </main>
  );
}
